/* eslint-disable @typescript-eslint/no-explicit-any */
import type { PropertyData, FormData as ProductFormData } from '../../AddProductPage/types'
import type { Product } from '../../../features/products/types'
import { serializeProductMedia } from '../../AddProductPage/utils/serializeMedia'

const generateUniqueId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const transformToUpdateFormat = (
  formData: ProductFormData,
  propertyData: PropertyData,
  _originalProduct: Product,
  materials: any[],
  colors: any[],
  status?: 'DRAFT' | 'PUBLISHED'
): FormData => {
  const multipartFormData = new FormData()
  const imageNames: string[] = []

  const sizes = propertyData.sizes.map((sizeData) => ({
    sizeId: sizeData.sizeId,
    additionalPrice: sizeData.price,
    additionalCostPrice: sizeData.additionalCostPrice,
    materials: sizeData.materials.map((materialData) => ({
      materialId:
        materials.find((m) => m.name === materialData.name)?.id || "string",
      additionalPrice: materialData.price,
      additionalCostPrice: materialData.additionalCostPrice,
      colours: materialData.colors.map((colorData) => {
        const colorName =
          colors.find((c) => c.hexValue === colorData.hex)?.name || "unknown";

        // Generate unique names for NEW images and add to FormData
        const colorImageNames =
          colorData.images?.map((image) => {
            const uniqueId = generateUniqueId();
            const imageName = `color-${colorName}-${uniqueId}`;
            multipartFormData.append("files", image, imageName);
            return imageName;
          }) || [];

        imageNames.push(...colorImageNames);

        // Combine existing imageUrls with new image names
        const allImageUrls = [
          ...(colorData.imageUrls || []),
          ...colorImageNames,
        ];

        return {
          colourId:
            colors.find((c) => c.hexValue === colorData.hex)?.id ||
            colorData.id,
          additionalPrice: colorData.price,
          additionalCostPrice: colorData.additionalCostPrice,
          quantity: colorData.quantity,
          weight: colorData.weight,
          imageUrls: allImageUrls,
          stockUpTrigger: 5,
        };
      }),
    })),
  }));

  // PE-1283 §7 E4 — replace the typed media gallery only when the form carries
  // media items. An empty/undefined gallery omits the key so existing products
  // (image-only, no gallery interaction) are untouched on the BFF.
  const media = serializeProductMedia(formData.media)

  const productData =
    status === "DRAFT"
      ? {
          name: formData.name,
          description: formData.details,
          categoryId: formData.category,
          sizeGuideId: formData.styleGuide || "string",
          basePrice: parseInt(formData.price) || 0,
          baseCostPrice: parseInt(formData.baseCostPrice) || 0,
          productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
          discountPercentage: formData.discountPercentage || 0,
          sizes,
          ...(media.length > 0 ? { media } : {}),
        }
      : {
          name: formData.name,
          description: formData.details,
          categoryId: formData.category,
          sizeGuideId: formData.styleGuide || "string",
          basePrice: parseInt(formData.price) || 0,
          baseCostPrice: parseInt(formData.baseCostPrice) || 0,
          productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
          discountPercentage: formData.discountPercentage || 0,
          status,
          sizes,
          ...(media.length > 0 ? { media } : {}),
        };

  multipartFormData.append('productData', JSON.stringify(productData))
  
  return multipartFormData
}